<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import {
  UiButton,
  UiIcon,
  UiInfoBox,
  UiSwitch,
  UiText,
  UiTextarea,
  UiChip,
  useApi,
  useToaster,
} from "@shopexperts/shared";
import type { AdminSetting, UpdateAdminSettingsRequest } from "@shopexperts/legacy-api";

const AI_SETTINGS_QUERY_KEY = ["admin", "settings", "ai_summaries"] as const;
const ENABLED_KEY = "ai_summaries.enabled";

const PROMPT_FIELDS: ReadonlyArray<{ key: string; label: string; rows: number; placeholder: string }> = [
  {
    key: "ai_summaries.system_prompt",
    label: "System prompt",
    rows: 6,
    placeholder: "You write concise, neutral summaries of Shopify experts for merchants comparing who to hire…",
  },
  {
    key: "ai_summaries.additional_context",
    label: "Additional context",
    rows: 4,
    placeholder: "shopexperts is a marketplace of vetted Shopify freelancers and agencies. Readers are store owners…",
  },
  {
    key: "ai_summaries.instructions",
    label: "Instructions",
    rows: 4,
    placeholder: "Two short paragraphs. Third person. No superlatives. Never invent numbers.",
  },
  {
    key: "ai_summaries.must_include",
    label: "Must include",
    rows: 3,
    placeholder: "Mention the expert's primary service area when the evidence supports it.",
  },
  {
    key: "ai_summaries.must_avoid",
    label: "Must avoid",
    rows: 3,
    placeholder: "Pricing claims, guarantees, comparisons with named competitors.",
  },
];

const api = useApi();
const toaster = useToaster();
const queryClient = useQueryClient();

const savedSettings = ref<AdminSetting[]>([]);
const drafts = ref<Record<string, string | boolean>>({});

const {
  data: queriedSettings,
  error: settingsError,
  isLoading,
  refetch,
} = useQuery({
  queryKey: AI_SETTINGS_QUERY_KEY,
  queryFn: () => api.admin.settings.list({ query: { groups: ["ai_summaries"] } }),
  staleTime: 60_000,
});

const { isPending: isSaving, mutateAsync: updateSettings } = useMutation({
  mutationFn: (body: UpdateAdminSettingsRequest) => api.admin.settings.update({ body }),
});

function hydrate(settings: AdminSetting[]) {
  savedSettings.value = settings;
  drafts.value = Object.fromEntries(settings.map((setting) => [setting.key, setting.value as string | boolean]));
}

watch(
  queriedSettings,
  (settings) => {
    if (!settings) return;
    if (savedSettings.value.length > 0 && dirtyKeys.value.length > 0) return;
    hydrate(settings);
  },
  { immediate: true },
);

const settingByKey = computed(() => new Map(savedSettings.value.map((setting) => [setting.key, setting])));
const enabledSetting = computed(() => settingByKey.value.get(ENABLED_KEY));
const isEnabledDraft = computed(() => drafts.value[ENABLED_KEY] === true);
const isEnabledSaved = computed(() => enabledSetting.value?.value === true);

const dirtyKeys = computed(() =>
  savedSettings.value.filter((setting) => drafts.value[setting.key] !== setting.value).map((setting) => setting.key),
);
const dirtyCount = computed(() => dirtyKeys.value.length);

function stringDraft(key: string): string {
  const value = drafts.value[key];
  return typeof value === "string" ? value : "";
}

function setDraft(key: string, value: string | boolean | undefined) {
  drafts.value[key] = value ?? "";
}

function discard() {
  hydrate(savedSettings.value);
}

async function save() {
  if (dirtyCount.value === 0 || isSaving.value) return;
  const settings = dirtyKeys.value.map((key) => ({ key, value: drafts.value[key] ?? "" }));
  try {
    const updated = await updateSettings({ settings });
    const updatedByKey = new Map(updated.map((setting) => [setting.key, setting]));
    hydrate(savedSettings.value.map((setting) => updatedByKey.get(setting.key) ?? setting));
    queryClient.invalidateQueries({ queryKey: ["admin", "settings"] });
    toaster.create({
      title: `${settings.length} ${settings.length === 1 ? "setting" : "settings"} saved`,
      type: "success",
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : "Unknown error";
    toaster.create({ title: `Failed to save: ${message}`, type: "error" });
  }
}

function formattedUpdatedAt(value: string | undefined): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `Updated ${date.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}`;
}
</script>

<template>
  <main class="min-w-0 flex-1 overflow-y-auto bg-secondary px-4 py-6 md:px-8 md:py-8">
    <div class="mx-auto flex w-full max-w-5xl flex-col gap-6">
      <header class="flex flex-col gap-5 border-b border-se-gray-300 pb-6 lg:flex-row lg:items-end lg:justify-between">
        <div class="flex max-w-3xl items-start gap-4">
          <div class="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-se-primary shadow-[3px_3px_0_0_#111827]">
            <UiIcon name="star-06" class="h-6 w-6 text-se-dark" />
          </div>
          <div class="flex flex-col gap-1.5">
            <UiText as="caption" variant="caption" class="text-se-gray-500" text="System control" />
            <UiText as="h1" variant="h2" class="font-semibold" text="AI summaries" />
            <UiText
              as="p"
              variant="p-small"
              class="max-w-2xl text-se-gray-600"
              text="The master switch and the global prompt every expert summary is written with. Per-expert context, generation, and revision history live on each expert's AI summaries tab."
            />
          </div>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <UiButton variant="ghost" label="Discard changes" :disabled="dirtyCount === 0 || isSaving" @click="discard" />
          <UiButton
            variant="primary"
            :label="isSaving ? 'Saving…' : dirtyCount > 0 ? `Save ${dirtyCount} ${dirtyCount === 1 ? 'change' : 'changes'}` : 'All changes saved'"
            :disabled="dirtyCount === 0 || isSaving"
            @click="save"
          />
        </div>
      </header>

      <UiInfoBox v-if="settingsError" variant="error" title="Couldn't load the AI summaries settings">
        <UiButton variant="outline" label="Try again" @click="() => refetch()" />
      </UiInfoBox>

      <div v-else-if="isLoading" class="flex flex-col gap-4">
        <div class="h-24 animate-pulse rounded-lg bg-se-gray-100" />
        <div class="h-96 animate-pulse rounded-lg bg-se-gray-100" />
      </div>

      <template v-else>
        <section class="flex flex-col gap-4 rounded-lg border border-se-gray-300 bg-white p-5 md:flex-row md:items-center md:justify-between">
          <div class="flex flex-col gap-1">
            <div class="flex items-center gap-2">
              <UiText as="h2" variant="h5" class="font-semibold" text="Master switch" />
              <UiChip
                :text="isEnabledSaved ? 'On' : 'Off'"
                :variant="isEnabledSaved ? 'success' : 'neutral'"
                size="xs"
                rounded="full"
              />
            </div>
            <UiText
              as="p"
              variant="p-small"
              class="max-w-2xl text-se-gray-600"
              :text="enabledSetting?.description ?? 'Master switch for AI-generated expert and reviews summaries.'"
            />
            <UiText
              v-if="enabledSetting"
              as="span"
              variant="caption-normal"
              class="text-se-gray-500"
              :text="formattedUpdatedAt(enabledSetting.updated_at)"
            />
          </div>
          <div class="flex items-center gap-3">
            <UiSwitch
              :model-value="isEnabledDraft"
              :disabled="isSaving || !enabledSetting"
              aria-label="Enable AI summaries"
              @update:model-value="setDraft(ENABLED_KEY, $event)"
            />
            <UiText
              as="span"
              variant="p-small"
              class="font-semibold"
              :class="isEnabledDraft ? 'text-se-green' : 'text-se-gray-500'"
              :text="isEnabledDraft ? 'Enabled' : 'Disabled'"
            />
          </div>
        </section>

        <UiInfoBox v-if="!isEnabledSaved" variant="warning" title="Nothing is generated or shown while the switch is off">
          <UiText
            as="p"
            variant="p-small"
            class="text-se-gray-700"
            text="Generation requests are refused and the public profile payload carries no summaries. Revisions that already exist are kept."
          />
        </UiInfoBox>

        <section class="flex flex-col gap-6 rounded-lg border border-se-gray-300 bg-white p-5">
          <div class="flex flex-col gap-1">
            <UiText as="h2" variant="h5" class="font-semibold" text="Global prompt" />
            <UiText
              as="p"
              variant="p-small"
              class="max-w-2xl text-se-gray-600"
              text="Every field here is authoritative: it overrides anything the expert's own data says. Changes apply to the next generation; existing summaries are not rewritten."
            />
          </div>

          <div v-for="field in PROMPT_FIELDS" :key="field.key" class="flex flex-col gap-1.5">
            <div class="flex flex-wrap items-center gap-2">
              <UiText as="label" variant="label" :for="field.key" :text="field.label" />
              <UiChip
                v-if="drafts[field.key] !== settingByKey.get(field.key)?.value"
                text="Unsaved"
                variant="warn"
                size="xs"
                rounded="full"
              />
            </div>
            <UiText
              as="p"
              variant="caption-normal"
              class="text-se-gray-500"
              :text="settingByKey.get(field.key)?.description ?? ''"
            />
            <UiTextarea
              :id="field.key"
              :name="field.key"
              :model-value="stringDraft(field.key)"
              :rows="field.rows"
              :placeholder="field.placeholder"
              :disabled="isSaving || !settingByKey.has(field.key)"
              @update:model-value="setDraft(field.key, $event)"
            />
          </div>
        </section>
      </template>

      <div
        v-if="dirtyCount > 0"
        class="sticky bottom-4 z-10 flex flex-col gap-3 rounded-lg border border-se-dark bg-se-dark px-4 py-3 text-white shadow-xl sm:flex-row sm:items-center sm:justify-between"
      >
        <UiText as="p" variant="p-small" :text="`${dirtyCount} unsaved ${dirtyCount === 1 ? 'change' : 'changes'}.`" />
        <UiButton variant="primary" theme="dark" :label="isSaving ? 'Saving…' : 'Save changes'" :disabled="isSaving" @click="save" />
      </div>
    </div>
  </main>
</template>
